/**
 * Utility functions for combining multiple form instances into a single view
 * and splitting them back on submission
 */

/**
 * Combines multiple form instances into a single form definition
 * Prefixes field names with form instance ID to avoid conflicts
 */
export const combineFormInstances = (formInstances) => {
  if (!formInstances || formInstances.length === 0) {
    return null;
  }

  const isResidencyInstance = (instance) => {
    const label = `${instance?.instance_name || ''} ${instance?.master_form_name || ''}`.toLowerCase();
    return label.includes('residency');
  };

  const hasMapField = (section) => Array.isArray(section?.fields) && section.fields.some((field) => field?.type === 'map');

  const orderSectionsForInstance = (instance, sections) => {
    if (!Array.isArray(sections) || sections.length <= 1) return sections || [];
    if (!isResidencyInstance(instance)) return sections;

    const mapSections = sections.filter((section) => hasMapField(section));
    const nonMapSections = sections.filter((section) => !hasMapField(section));
    return [...mapSections, ...nonMapSections];
  };

  // Keep existing relative order, but surface residency form first in combined flow.
  const orderedFormInstances = formInstances
    .map((instance, index) => ({ instance, index }))
    .sort((a, b) => {
      const aResidency = isResidencyInstance(a.instance);
      const bResidency = isResidencyInstance(b.instance);
      if (aResidency === bResidency) return a.index - b.index;
      return aResidency ? -1 : 1;
    })
    .map((entry) => entry.instance);

  const combinedSections = [];
  const formInstanceMap = {}; // Maps prefixed field names to form instance IDs

  orderedFormInstances.forEach((instance) => {
    const instanceId = instance.id;
    let formData = instance.form_data;

    // Parse form_data if it's a string
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        console.error(`Failed to parse form_data for instance ${instanceId}:`, e);
        return; // Skip this instance
      }
    }

    if (!formData || !formData.sections) {
      return; // Skip if no sections
    }

    // Add form title section
    combinedSections.push({
      section_name: instance.instance_name || instance.master_form_name,
      section_note: instance.instance_description || null,
      is_form_header: true,
      form_instance_id: instanceId,
      is_required: instance.is_required || false,
      fields: []
    });

    const orderedSections = orderSectionsForInstance(instance, formData.sections);

    // Process each section
    orderedSections.forEach((section, sectionIndex) => {
      // Skip admin-only sections for parent view
      if (section.admin_only) {
        return;
      }

      // Handle sections with allows_multiple (repeating groups)
      if (section.allows_multiple) {
        // Create a single repeatable_group field for the entire section
        const repeatableField = {
          name: `${instanceId}_repeating_group_${sectionIndex}`,
          label: section.section_name,
          type: 'repeatable_group',
          allows_multiple: true,
          min_items: 0,
          max_items: 10,
          form_instance_id: instanceId,
          fields: []
        };

        // Process fields within the repeating group
        section.fields.forEach((field) => {
          // Skip admin-only fields
          if (field.admin_only) {
            return;
          }

          // Apply field overrides if they exist
          const fieldOverrides = instance.field_overrides || {};
          const override = fieldOverrides[field.name];

          // Skip hidden fields
          if (override && override.is_hidden === true) {
            return;
          }

          // Determine if field is required (override takes precedence)
          const isRequired = override && override.is_required !== null && override.is_required !== undefined
            ? override.is_required
            : field.required || false;

          repeatableField.fields.push({
            ...field,
            name: field.name, // Keep original name within the group
            required: isRequired // Apply override
          });
        });

        const combinedSection = {
          section_name: section.section_name,
          section_note: section.section_note,
          form_instance_id: instanceId,
          fields: [repeatableField]
        };

        combinedSections.push(combinedSection);
        return;
      }

      const combinedSection = {
        section_name: section.section_name,
        section_note: section.section_note,
        form_instance_id: instanceId,
        fields: []
      };

      // Process fields and prefix names
      section.fields.forEach((field) => {
        // Skip admin-only fields
        if (field.admin_only) {
          return;
        }

        // Apply field overrides if they exist
        const fieldOverrides = instance.field_overrides || {};
        const override = fieldOverrides[field.name];

        // Skip hidden fields
        if (override && override.is_hidden === true) {
          return;
        }

        // Determine if field is required (override takes precedence)
        const isRequired = override && override.is_required !== null && override.is_required !== undefined
          ? override.is_required
          : field.required || false;

        const prefixedField = {
          ...field,
          name: `${instanceId}_${field.name}`,
          original_name: field.name, // Keep original for splitting
          form_instance_id: instanceId,
          required: isRequired // Apply override
        };

        // Update conditional field references
        if (prefixedField.conditional && prefixedField.conditional.field) {
          // Prefix the conditional field reference with the same instance ID
          const conditionalFieldName = prefixedField.conditional.field;
          // Only prefix if it's not already prefixed (doesn't start with a UUID pattern)
          if (!conditionalFieldName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i)) {
            prefixedField.conditional.field = `${instanceId}_${conditionalFieldName}`;
          }
        }

        combinedSection.fields.push(prefixedField);
        formInstanceMap[prefixedField.name] = instanceId;
      });

      combinedSections.push(combinedSection);
    });
  });


  return {
    sections: combinedSections,
    formInstanceMap,
    formInstances: orderedFormInstances.map(inst => ({
      id: inst.id,
      instance_name: inst.instance_name,
      master_form_name: inst.master_form_name,
      allows_multiple: inst.form_data?.allows_multiple || false
    }))
  };
};

/**
 * Splits combined form data back into individual form instance data
 * Special handling: Siblings data should be copied to ALL form instances that have a siblings section
 */
export const splitFormDataByInstance = (combinedData, formInstanceMap, formInstances) => {
  const splitData = {};

  // First pass: Extract fields that belong to each instance
  formInstances.forEach((instance) => {
    const instanceId = instance.id;
    const instanceData = {};

    // Extract fields that belong to this instance
    Object.keys(combinedData).forEach((prefixedFieldName) => {
      if (prefixedFieldName.startsWith(`${instanceId}_`)) {
        const originalFieldName = prefixedFieldName.replace(`${instanceId}_`, '');
        instanceData[originalFieldName] = combinedData[prefixedFieldName];
      }
    });

    splitData[instanceId] = instanceData;
  });

  // Second pass: Find siblings data and copy it to all form instances that have a siblings section
  // Find all form instances that have a siblings section
  const instancesWithSiblings = [];
  formInstances.forEach((instance) => {
    let formData = instance.form_data;
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        return; // Skip if can't parse
      }
    }
    
    if (formData && formData.sections) {
      formData.sections.forEach((section, sectionIndex) => {
        if (section.allows_multiple && 
            section.section_name && 
            section.section_name.toLowerCase().includes('sibling')) {
          instancesWithSiblings.push({
            instanceId: instance.id,
            sectionIndex: sectionIndex,
            fieldName: `repeating_group_${sectionIndex}`
          });
        }
      });
    }
  });

  // If we found multiple instances with siblings sections, find siblings data from any instance
  if (instancesWithSiblings.length > 1) {
    let siblingsData = null;
    let sourceInstanceId = null;

    // Find siblings data from any form instance
    instancesWithSiblings.forEach(({ instanceId, fieldName }) => {
      const prefixedFieldName = `${instanceId}_${fieldName}`;
      if (combinedData[prefixedFieldName] && Array.isArray(combinedData[prefixedFieldName])) {
        siblingsData = combinedData[prefixedFieldName];
        sourceInstanceId = instanceId;
      }
    });

    // If we found siblings data, copy it to all instances with siblings sections
    if (siblingsData && siblingsData.length > 0) {
      instancesWithSiblings.forEach(({ instanceId, fieldName }) => {
        // Only copy if this instance doesn't already have siblings data
        if (!splitData[instanceId][fieldName] || 
            !Array.isArray(splitData[instanceId][fieldName]) || 
            splitData[instanceId][fieldName].length === 0) {
          splitData[instanceId][fieldName] = siblingsData;
          console.log(`[splitFormDataByInstance] Copied siblings data from ${sourceInstanceId} to ${instanceId} (${siblingsData.length} siblings)`);
        }
      });
    }
  }

  return splitData;
};

/**
 * Extracts student information from form data (from New Student Form)
 */
export const extractStudentInfo = (formData, formInstanceMap, formInstances = []) => {
  // Find the New Student Form instance by name
  const newStudentFormInstance = formInstances.find(inst => 
    inst.instance_name?.toLowerCase().includes('new student') ||
    inst.master_form_name?.toLowerCase().includes('new student')
  );

  const targetInstanceId = newStudentFormInstance?.id;

  // Look for student fields in the combined data
  const studentFields = ['legal_name_first', 'legal_name_last', 'first_name', 'last_name', 
                         'age', 'gender', 'grade', 'date_of_birth', 'primary_address_street',
                         'primary_address_city', 'primary_address_state', 'primary_address_zip',
                         'ethnicity_race'];

  const studentInfo = {};

  // Search through all prefixed fields
  Object.keys(formData).forEach((prefixedFieldName) => {
    // Extract instance ID and original field name
    const parts = prefixedFieldName.split('_');
    if (parts.length < 2) return; // Skip if not prefixed
    
    // Try to find UUID pattern (first 8-4-4-4-12 pattern)
    let instanceId = null;
    let originalFieldName = null;
    
    // Check if it starts with a UUID pattern
    const uuidPattern = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_(.+)$/i;
    const match = prefixedFieldName.match(uuidPattern);
    
    if (match) {
      instanceId = match[1];
      originalFieldName = match[2];
    } else {
      // Fallback: assume first part is instance ID (if we have targetInstanceId)
      if (targetInstanceId && prefixedFieldName.startsWith(`${targetInstanceId}_`)) {
        instanceId = targetInstanceId;
        originalFieldName = prefixedFieldName.replace(`${targetInstanceId}_`, '');
      } else {
        // Last resort: take everything after first underscore
        originalFieldName = parts.slice(1).join('_');
      }
    }
    
    // Only process fields from the New Student Form instance (if we found it)
    if (targetInstanceId && instanceId !== targetInstanceId) {
      return;
    }
    
    if (studentFields.includes(originalFieldName)) {
      const value = formData[prefixedFieldName];

      // Map to student table fields
      if (originalFieldName === 'legal_name_first' || originalFieldName === 'first_name') {
        studentInfo.first_name = value || studentInfo.first_name;
      } else if (originalFieldName === 'legal_name_last' || originalFieldName === 'last_name') {
        studentInfo.last_name = value || studentInfo.last_name;
      } else if (originalFieldName === 'age') {
        studentInfo.age = value;
      } else if (originalFieldName === 'gender') {
        studentInfo.gender = value;
      } else if (originalFieldName === 'grade') {
        studentInfo.grade = value;
      } else if (originalFieldName === 'date_of_birth') {
        studentInfo.date_of_birth = value;
      } else if (originalFieldName === 'ethnicity_race') {
        studentInfo.ethnicity_race = Array.isArray(value) ? value.join(', ') : value;
      }
    }

    // Build address
    if (originalFieldName === 'primary_address_street') {
      studentInfo.address_street = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_address_city') {
      studentInfo.address_city = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_address_state') {
      studentInfo.address_state = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_address_zip') {
      studentInfo.address_zip = formData[prefixedFieldName];
    }
  });

  // Combine address
  if (studentInfo.address_street) {
    const addressParts = [
      studentInfo.address_street,
      studentInfo.address_city,
      studentInfo.address_state,
      studentInfo.address_zip
    ].filter(Boolean);
    studentInfo.address = addressParts.join(', ');
  }

  return studentInfo;
};

